import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ProposalHeader } from '../components/ProposalHeader';
import { MilestonesTab } from '../components/MilestonesTab';
import { RequirementsTab } from '../components/RequirementsTab';
import { ArtifactsTab } from '../components/ArtifactsTab';
import { GrantsmanshipTab } from '../components/GrantsmanshipTab';
import { TasksTab } from '../components/TasksTab';
import { ExportPacketModal } from '../components/ExportPacketModal';
import { Milestone, Lightbulb, FileBox, CheckSquare, PenLine, Loader2, Sparkles, Upload, RotateCcw } from 'lucide-react';
import { existingProposalFixture, freshProposalFixture, createPostUploadFixture } from '../lib/fixtures';
import { ProposalData, ArtifactCategoryData, CriterionData } from '../lib/types';
import { adminProposals } from '../lib/adminFixtures';
import { AdminProposalSummary } from '../lib/adminTypes';
interface ProposalDetailPageProps {
  proposalData?: ProposalData;
}
export function ProposalDetailPage({
  proposalData: propData
}: ProposalDetailPageProps) {
  const {
    id,
    tab
  } = useParams();
  const navigate = useNavigate();
  const isAdminView = localStorage.getItem('viewMode') === 'admin';
  const location = useLocation();
  // Export modal state for admin view
  const [showExportModal, setShowExportModal] = useState(false);
  const adminProposal = isAdminView && id ? adminProposals.find(p => p.id === id) || null : null;
  // Upload simulation state
  const [hasUploaded, setHasUploaded] = useState(localStorage.getItem('proposalUploaded_10') === 'true');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const [analyzeStep, setAnalyzeStep] = useState(0);
  const analyzeSteps = ['Uploading documents...', 'Parsing document structure...', 'Checking formatting compliance...', 'Evaluating scope alignment...', 'Scanning for disqualification risks...', 'Generating grantsmanship feedback...', 'Creating action items...', 'Finalizing analysis...'];
  // Per-artifact upload state
  const [uploadedArtifactIds, setUploadedArtifactIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('uploadedArtifacts_10');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [uploadingArtifactId, setUploadingArtifactId] = useState<string | null>(null);
  const handleArtifactUpload = (artifactId: string) => {
    if (uploadingArtifactId) return; // prevent concurrent uploads
    setUploadingArtifactId(artifactId);
    setTimeout(() => {
      setUploadingArtifactId(null);
      const newIds = [...uploadedArtifactIds, artifactId];
      setUploadedArtifactIds(newIds);
      localStorage.setItem('uploadedArtifacts_10', JSON.stringify(newIds));
    }, 2000);
  };
  const handleRunAnalysis = () => {
    setIsAnalyzing(true);
    setAnalyzeProgress(0);
    setAnalyzeStep(0);
    const duration = 20000;
    const interval = 80;
    const steps = duration / interval;
    const increment = 100 / steps;
    let currentProgress = 0;
    const timer = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(timer);
        setTimeout(() => {
          localStorage.setItem('proposalUploaded_10', 'true');
          setHasUploaded(true);
          setIsAnalyzing(false);
        }, 400);
      }
      setAnalyzeProgress(currentProgress);
      const stepIndex = Math.min(Math.floor(currentProgress / 100 * analyzeSteps.length), analyzeSteps.length - 1);
      setAnalyzeStep(stepIndex);
    }, interval);
  };
  // Loading state for new proposal (id=10)
  const alreadyCreated = localStorage.getItem('newProposalCreated') === 'true';
  const [isThinking, setIsThinking] = useState(id === '10' && !alreadyCreated);
  const [progress, setProgress] = useState(id === '10' && !alreadyCreated ? 0 : 100);
  const [thinkingStep, setThinkingStep] = useState(0);
  const thinkingSteps = ['Analyzing funding opportunity...', 'Extracting key requirements...', 'Identifying milestones...', 'Generating artifact checklist...', 'Setting up compliance rules...', 'Finalizing workspace...'];
  useEffect(() => {
    if (id !== '10' || alreadyCreated) return;
    const duration = 20000;
    const interval = 100;
    const steps = duration / interval;
    const increment = 100 / steps;
    let currentProgress = 0;
    const timer = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(timer);
        setTimeout(() => {
          localStorage.setItem('newProposalCreated', 'true');
          setIsThinking(false);
        }, 500);
      }
      setProgress(currentProgress);
      const stepIndex = Math.min(Math.floor(currentProgress / 100 * thinkingSteps.length), thinkingSteps.length - 1);
      setThinkingStep(stepIndex);
    }, interval);
    return () => clearInterval(timer);
  }, [id, alreadyCreated]);
  // Determine which data to use — MUST be above early return so hooks below always run
  const data: ProposalData = useMemo(() => {
    let result: ProposalData = existingProposalFixture;
    if (propData) {
      result = propData;
    } else if (id === '10') {
      result = hasUploaded ? createPostUploadFixture(freshProposalFixture) : freshProposalFixture;
      // Override header with wizard-entered title if available
      const savedTitle = localStorage.getItem('newProposalTitle');
      if (savedTitle) {
        result = {
          ...result,
          header: {
            ...result.header,
            title: `${result.header.funder} ${result.header.mechanism}: ${savedTitle}`
          }
        };
      }
    } else if (id === '1') {
      result = existingProposalFixture;
    }
    // Restore persisted ready statuses from localStorage
    if (id) {
      try {
        const stored = localStorage.getItem(`artifactsReadyIds_${id}`);
        if (stored) {
          const readyIds: string[] = JSON.parse(stored);
          if (readyIds.length > 0) {
            result = {
              ...result,
              artifactCategories: result.artifactCategories.map(cat => ({
                ...cat,
                artifacts: cat.artifacts.map(a => readyIds.includes(a.id) ? {
                  ...a,
                  readyStatus: true
                } : a),
                completedCount: cat.artifacts.filter(a => readyIds.includes(a.id) ? true : a.readyStatus).length
              }))
            };
          }
        }
      } catch {
        // ignore parse errors
      }
    }
    return result;
  }, [id, hasUploaded, propData]);
  // Build artifact ID → title map for RequirementsTab
  const artifactNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    data.artifactCategories.forEach(cat => {
      cat.artifacts.forEach(a => {
        map[a.id] = a.title;
      });
    });
    return map;
  }, [data]);
  // ALL useState/useEffect hooks MUST be above the early return
  const [localTab, setLocalTab] = useState('milestones');
  const [artifactCategories, setArtifactCategories] = useState<ArtifactCategoryData[]>(data.artifactCategories);
  useEffect(() => {
    setArtifactCategories(data.artifactCategories);
  }, [data]);
  // Now safe to early return — all hooks have been called
  if (isThinking) {
    return <div className="h-full bg-white flex flex-col items-center justify-center p-6" data-id="element-1179">
        <div className="w-full max-w-md space-y-8 text-center" data-id="element-1180">
          <div className="relative" data-id="element-1181">
            <div className="absolute inset-0 flex items-center justify-center opacity-10" data-id="element-1182">
              <Sparkles className="h-32 w-32 text-indigo-600 animate-pulse" data-id="element-1183" />
            </div>
            <div className="relative z-10 h-20 w-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1184">
              <Loader2 className="h-10 w-10 text-indigo-600 animate-spin" data-id="element-1185" />
            </div>
          </div>
          <div className="space-y-4" data-id="element-1186">
            <h2 className="text-2xl font-bold text-foreground" data-id="element-1187">
              Building your proposal workspace
            </h2>
            <p className="text-muted-foreground h-6 transition-all duration-300" data-id="element-1188">
              {thinkingSteps[thinkingStep]}
            </p>
          </div>
          <div className="space-y-2" data-id="element-1189">
            <Progress value={progress} className="h-2" data-id="element-1190" />
            <div className="flex justify-between text-xs text-muted-foreground px-1" data-id="element-1191">
              <span data-id="element-1192">0%</span>
              <span data-id="element-1193">{Math.round(progress)}%</span>
            </div>
          </div>
        </div>
      </div>;
  }
  // Analyzing overlay for upload simulation
  if (isAnalyzing) {
    return <div className="h-full bg-white flex flex-col items-center justify-center p-6" data-id="element-1194">
        <div className="w-full max-w-md space-y-8 text-center" data-id="element-1195">
          <div className="relative" data-id="element-1196">
            <div className="absolute inset-0 flex items-center justify-center opacity-10" data-id="element-1197">
              <Sparkles className="h-32 w-32 text-violet-600 animate-pulse" data-id="element-1198" />
            </div>
            <div className="relative z-10 h-20 w-20 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1199">
              <Loader2 className="h-10 w-10 text-violet-600 animate-spin" data-id="element-1200" />
            </div>
          </div>
          <div className="space-y-4" data-id="element-1201">
            <h2 className="text-2xl font-bold text-foreground" data-id="element-1202">
              Running AI analysis
            </h2>
            <p className="text-muted-foreground h-6 transition-all duration-300" data-id="element-1203">
              {analyzeSteps[analyzeStep]}
            </p>
          </div>
          <div className="space-y-2" data-id="element-1204">
            <Progress value={analyzeProgress} className="h-2" data-id="element-1205" />
            <div className="flex justify-between text-xs text-muted-foreground px-1" data-id="element-1206">
              <span data-id="element-1207">0%</span>
              <span data-id="element-1208">{Math.round(analyzeProgress)}%</span>
            </div>
          </div>
        </div>
      </div>;
  }
  // Map URL tab param to internal Tabs value
  const tabMap: Record<string, string> = {
    timeline: 'milestones',
    criteria: 'requirements',
    artifacts: 'artifacts',
    grantsmanship: 'grantsmanship',
    tasks: 'review'
  };
  // Reverse map for navigation
  const routeMap: Record<string, string> = {
    milestones: 'timeline',
    requirements: 'criteria',
    artifacts: 'artifacts',
    grantsmanship: 'grantsmanship',
    review: 'tasks'
  };
  const currentTab = id ? tab && tabMap[tab] ? tabMap[tab] : 'milestones' : localTab;
  const handleTabChange = (value: string) => {
    if (id) {
      const route = routeMap[value];
      if (route) {
        navigate(`/proposals/${id}/${route}`);
      }
    } else {
      setLocalTab(value);
    }
  };
  // Helper to calculate counts for badges
  const getCounts = (data: ProposalData) => {
    return {
      milestones: data.milestones.filter(m => m.status === 'overdue').length,
      criteria: {
        notSatisfied: data.criteria.filter(c => c.status === 'not-satisfied').length,
        unknown: data.criteria.filter(c => c.status === 'unknown').length
      },
      artifacts: {
        notSatisfied: data.artifactCategories.reduce((acc, cat) => acc + cat.artifacts.filter(a => a.complianceStatus === 'not-satisfied').length, 0),
        unknown: data.artifactCategories.reduce((acc, cat) => acc + cat.artifacts.filter(a => a.complianceStatus === 'unknown').length, 0)
      },
      grantsmanship: data.grantsmanshipSections.reduce((acc, sec) => acc + sec.findings.length, 0),
      tasks: {
        overdue: data.tasks.filter(t => t.isOverdue).length,
        open: data.tasks.filter(t => t.status === 'open').length
      }
    };
  };
  const counts = getCounts(data);
  const handleMarkReady = (artifactId: string) => {
    setArtifactCategories(prev => {
      const updated = prev.map(cat => ({
        ...cat,
        artifacts: cat.artifacts.map(a => a.id === artifactId ? {
          ...a,
          readyStatus: !a.readyStatus
        } : a)
      }));
      // Persist ready IDs, count, and total for dashboard
      if (id) {
        const readyIds = updated.flatMap(cat => cat.artifacts.filter(a => a.readyStatus).map(a => a.id));
        const totalCount = updated.reduce((acc, cat) => acc + cat.artifacts.length, 0);
        localStorage.setItem(`artifactsReadyIds_${id}`, JSON.stringify(readyIds));
        localStorage.setItem(`artifactsReady_${id}`, String(readyIds.length));
        localStorage.setItem(`artifactsTotal_${id}`, String(totalCount));
      }
      return updated;
    });
  };
  const artifactsTotal = artifactCategories.reduce((acc, cat) => acc + cat.artifacts.length, 0);
  const artifactsCompleted = artifactCategories.reduce((acc, cat) => acc + cat.artifacts.filter(a => a.readyStatus).length, 0);
  return <div className="h-full flex flex-col bg-muted/40 overflow-hidden" data-id="element-1209">
      <Tabs value={currentTab} onValueChange={handleTabChange} className="flex flex-col flex-1 min-h-0" data-id="element-1210">
        {/* Sticky header area — does not scroll */}
        <div className="flex-shrink-0" data-id="element-1211">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-5 pb-3 space-y-4" data-id="element-1212">
            <ProposalHeader data={data.header} artifactsCompleted={artifactsCompleted} artifactsTotal={artifactsTotal} data-id="element-1213" />

            <div className="flex items-center gap-3" data-id="element-1214">
              <TabsList className="flex-1 justify-start bg-white border shadow-sm h-11 p-1 rounded-lg overflow-x-auto" data-id="element-1215">
                <TabsTrigger value="milestones" className="gap-1.5 text-xs sm:text-sm data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 data-[state=active]:shadow-none px-3" data-id="element-1216">
                  <Milestone className="h-3.5 w-3.5" data-id="element-1217" />
                  Timeline
                  {counts.milestones > 0 && <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-100 text-[10px] font-semibold text-red-700 h-5 min-w-[20px] px-1" data-id="element-1218">
                      {counts.milestones}
                    </span>}
                </TabsTrigger>
                <TabsTrigger value="requirements" className="gap-1.5 text-xs sm:text-sm data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 data-[state=active]:shadow-none px-3" data-id="element-1219">
                  <Lightbulb className="h-3.5 w-3.5" data-id="element-1220" />
                  Criteria
                  {counts.criteria.notSatisfied > 0 && <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-100 text-[10px] font-semibold text-red-700 h-5 min-w-[20px] px-1" data-id="element-1221">
                      {counts.criteria.notSatisfied}
                    </span>}
                </TabsTrigger>
                <TabsTrigger value="artifacts" className="gap-1.5 text-xs sm:text-sm data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 data-[state=active]:shadow-none px-3" data-id="element-1222">
                  <FileBox className="h-3.5 w-3.5" data-id="element-1223" />
                  Documents
                  {counts.artifacts.notSatisfied > 0 && <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-100 text-[10px] font-semibold text-red-700 h-5 min-w-[20px] px-1" data-id="element-1224">
                      {counts.artifacts.notSatisfied}
                    </span>}
                </TabsTrigger>
                <TabsTrigger value="grantsmanship" className="gap-1.5 text-xs sm:text-sm data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 data-[state=active]:shadow-none px-3" data-id="element-1225">
                  <PenLine className="h-3.5 w-3.5" data-id="element-1226" />
                  Grantsmanship
                  {counts.grantsmanship > 0 && <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-100 h-5 w-5" data-id="element-1227">
                      <span className="h-[6px] w-[6px] rounded-full bg-red-700" data-id="element-1228" />
                    </span>}
                </TabsTrigger>
                <TabsTrigger value="review" className="gap-1.5 text-xs sm:text-sm data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 data-[state=active]:shadow-none px-3" data-id="element-1229">
                  <CheckSquare className="h-3.5 w-3.5" data-id="element-1230" />
                  Tasks
                  {counts.tasks.overdue > 0 && <span className="ml-1 inline-flex items-center justify-center rounded-full bg-red-100 text-[10px] font-semibold text-red-700 h-5 min-w-[20px] px-1" data-id="element-1231">
                      {counts.tasks.overdue}
                    </span>}
                </TabsTrigger>
              </TabsList>

              {/* Re-run Analysis button — shows when artifacts uploaded but analysis not yet run */}
              {id === '10' && uploadedArtifactIds.length > 0 && !hasUploaded && <button onClick={handleRunAnalysis} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 text-xs font-semibold hover:bg-violet-100 transition-colors shadow-sm flex-shrink-0 h-11" data-id="element-1232">
                    <RotateCcw className="h-3.5 w-3.5" data-id="element-1233" />
                    Re-run Analysis
                    <span className="inline-flex items-center gap-0.5 bg-violet-100 text-violet-700 border border-violet-200 text-[10px] px-1.5 py-0 h-5 rounded-full font-semibold" data-id="element-1234">
                      <Sparkles className="h-3 w-3" data-id="element-1235" />
                      AI
                    </span>
                  </button>}
            </div>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto min-h-0" data-id="element-1236">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-6" data-id="element-1237">
            <TabsContent value="milestones" className="mt-0" data-id="element-1238">
              <MilestonesTab milestones={data.milestones} data-id="element-1239" />
            </TabsContent>

            <TabsContent value="requirements" className="mt-0" data-id="element-1240">
              <RequirementsTab criteria={data.criteria} artifactNameMap={artifactNameMap} data-id="element-1241" />
            </TabsContent>

            <TabsContent value="artifacts" className="mt-0" data-id="element-1242">
              <ArtifactsTab categories={artifactCategories} criteria={data.criteria} onMarkReady={handleMarkReady} onUploadArtifact={id === '10' && !hasUploaded ? handleArtifactUpload : undefined} uploadingArtifactId={uploadingArtifactId} uploadedArtifactIds={id === '10' && !hasUploaded ? uploadedArtifactIds : undefined} isAdmin={isAdminView} onExport={adminProposal ? () => setShowExportModal(true) : undefined} data-id="element-1243" />
            </TabsContent>

            <TabsContent value="grantsmanship" className="mt-0" data-id="element-1244">
              <GrantsmanshipTab sections={data.grantsmanshipSections} data-id="element-1245" />
            </TabsContent>

            <TabsContent value="review" className="mt-0" data-id="element-1246">
              <TasksTab tasks={data.tasks} inboxEmail={id === '10' ? 'NIH-R21-multisensory-2026@origamigants.com' : 'NIH-R21-alzheimers-2026@origamigrants.com'} data-id="element-1247" />
            </TabsContent>
          </div>
        </div>
      </Tabs>

      {showExportModal && adminProposal && <ExportPacketModal proposal={adminProposal} open={showExportModal} onClose={() => setShowExportModal(false)} data-id="element-1248" />}
    </div>;
}